/* useProjectActivity — 4-second polling hook backed by the unified
 * /api/projects/:id/activity endpoint.
 *
 * Why this exists
 * ---------------
 * Multiple post-processing tasks (DerivedCCC, DirectorPromptFanOut,
 * DopSpec auto-fire, AssetBindings, Stage 5.5 batch+autofix) run AFTER the
 * primary "X done" signal each stage page was polling. The per-stage poll
 * loops stop the moment the primary flag flips, leaving the UI stale until
 * the user hits hard-refresh. This hook subscribes to a single signature
 * that changes whenever ANY project-level data has changed, and fires
 * `onChange()` so the calling component can refetch its own data.
 *
 * Usage
 * -----
 * useProjectActivity(projectId, () => loadProjectData())
 *
 * The hook is no-op if `projectId` is falsy. Polling stops automatically
 * on unmount or when the tab is hidden, and resumes on visibility return.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_INTERVAL_MS = 4000;

export function useProjectActivity(projectId, onChange) {
  const [activity, setActivity] = useState({
    any_running: false,
    running_tasks: [],
    signature: null,
    shots_count: 0,
  });
  const lastSigRef = useRef(null);
  // Always call the latest onChange — useRef avoids re-subscribing the
  // polling effect on every parent re-render. (Without this, setState in
  // ProjectView would tear down + recreate the interval each tick.)
  const onChangeRef = useRef(onChange);
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    let timer = null;

    const tick = async () => {
      if (cancelled) return;
      // Pause polling when tab is hidden — saves bandwidth and DB hits
      if (typeof document !== "undefined" && document.visibilityState === "hidden") {
        timer = setTimeout(tick, POLL_INTERVAL_MS);
        return;
      }
      try {
        const r = await axios.get(`${API}/projects/${projectId}/activity`);
        if (cancelled) return;
        const next = r.data;
        setActivity(next);
        if (lastSigRef.current && next.signature && next.signature !== lastSigRef.current) {
          // Project data changed (a task finished, new shots arrived, etc.)
          try { onChangeRef.current?.(next); } catch (e) { /* swallow caller errors */ }
        }
        lastSigRef.current = next.signature;
      } catch { /* polling errors are non-fatal */ }
      if (!cancelled) timer = setTimeout(tick, POLL_INTERVAL_MS);
    };

    tick();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [projectId]);

  return activity;
}
